// ═══ EvalInspector — 내 프로필 / 정산 / 등급 ═══
// EvalInspectorPage.js에서 분리.
import React, { useState, useEffect } from 'react';
import { API_BASE, LEVELS, LvBadge, Stars } from './EvalInspectorShared';

function ProfileView({ inspector, onBack, onLogout }) {
  const avg = inspector.avg_rating;
  const reviews = inspector.reviews || [];
  var [mySettlements, setMySettlements] = useState([]);
  // Wave S5(b) (2026-05-21) — PDF 다운로드 중복 클릭 차단
  var [downloadingId, setDownloadingId] = useState(null);

  useEffect(function() {
    fetch(API_BASE + '/api/eval/settlements/my/' + inspector.id).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setMySettlements(d.data || []);
    }).catch(function() {});
  }, [inspector.id]);

  // Wave S5(b) (2026-05-21) — 본인 정산서 PDF 다운로드. inspector_id 기반 토큰 발급.
  //   기존 /settlements/my/:inspector_id 와 동일 보안 모델 (PIN 재확인 없음).
  var handleDownloadPdf = function(settlementId) {
    setDownloadingId(settlementId);
    fetch(API_BASE + '/api/eval/settlements/' + settlementId + '/download-token', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inspector_id: inspector.id }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success && d.token) {
        var url = API_BASE + '/api/eval/settlements/' + settlementId + '/pdf?token=' + encodeURIComponent(d.token);
        var a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert('PDF 다운로드 실패: ' + (d && d.error ? d.error : '알 수 없는 오류'));
      }
    }).catch(function(e) {
      alert('PDF 다운로드 네트워크 오류: ' + (e && e.message ? e.message : e));
    }).finally(function() { setDownloadingId(null); });
  };

  const avgByKey = (key) => {
    if (!reviews.length) return 0;
    // mysql DECIMAL → string '+' concat 위험 대응 (Number 강제)
    return +(reviews.reduce((s, r) => s + Number(r[key] || 0), 0) / reviews.length).toFixed(1);
  };

  const bar = (val) => ({
    height: 6, borderRadius: 3,
    background: val >= 4.5 ? '#1D9E75' : val >= 3.5 ? '#EF9F27' : '#E24B4A',
    width: `${(val / 5) * 100}%`,
  });

  const distrib = [5, 4, 3, 2, 1].map(s => ({ s, c: reviews.filter(r => r.score === s).length }));
  const maxC = Math.max(...distrib.map(d => d.c), 1);

  const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 16, marginBottom: 10 };

  return (
    <div style={{ padding: '12px 16px', paddingBottom: 40 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 15, color: '#3B8BD4', padding: 0, marginBottom: 12, fontWeight: 600, cursor: 'pointer' }}>← 뒤로</button>

      {/* 프로필 헤더 */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: avg >= 4.5 ? '#FAECE7' : '#EEEDFE',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 800, color: avg >= 4.5 ? '#D85A30' : '#534AB7',
          }}>{inspector.name[0]}</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#111' }}>{inspector.name}</span>
              <LvBadge level={inspector.level} />
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>{inspector.area} · {inspector.phone}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[
            [avg, '평균 평점', '#EF9F27'],
            [inspector.total_jobs, '총 검차', '#222'],
            [reviews.length, '리뷰', '#222'],
          ].map(([v, l, c], i) => (
            <div key={i} style={{ background: '#f8f8f5', borderRadius: 8, padding: '10px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</div>
              <div style={{ fontSize: 10, color: '#aaa' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 항목별 평점 */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#222' }}>항목별 평점</div>
        {[['신속성', avgByKey('speed')], ['정확성', avgByKey('accuracy')], ['꼼꼼함', avgByKey('detail')]].map(([l, v]) => (
          <div key={l} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: '#888' }}>{l}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>{v}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: '#f0f0ec' }}><div style={bar(v)} /></div>
          </div>
        ))}
      </div>

      {/* 평점 분포 */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#222' }}>평점 분포</div>
        {distrib.map(d => (
          <div key={d.s} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <span style={{ fontSize: 12, color: '#EF9F27', width: 16, textAlign: 'right' }}>{d.s}★</span>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#f0f0ec' }}>
              <div style={{ height: 8, borderRadius: 4, background: '#EF9F27', width: `${(d.c / maxC) * 100}%` }} />
            </div>
            <span style={{ fontSize: 11, color: '#aaa', width: 16 }}>{d.c}</span>
          </div>
        ))}
      </div>

      {/* 등급 기준 */}
      <div style={{ ...cardStyle, background: '#f8f8f5', border: 'none' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#222' }}>등급 기준</div>
        {[
          ['마스터', '100건+ & 4.5+', '검차비 +20%'],
          ['시니어', '50건+ & 4.0+', '검차비 +10%'],
          ['일반', '기본', '기본 검차비'],
        ].map(([lv, cond, benefit]) => (
          <div key={lv} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: lv !== '일반' ? '1px solid #eee' : 'none' }}>
            <LvBadge level={lv} />
            <div style={{ flex: 1, fontSize: 12, color: '#666' }}>{cond}</div>
            <span style={{ fontSize: 11, color: inspector.level === lv ? LEVELS[lv].color : '#aaa', fontWeight: inspector.level === lv ? 700 : 400 }}>
              {inspector.level === lv ? '현재' : benefit}
            </span>
          </div>
        ))}
      </div>

      {/* 최근 리뷰 */}
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, marginTop: 6, color: '#222' }}>최근 리뷰</div>
      {reviews.map((r, i) => (
        <div key={i} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <Stars value={r.score} size={14} />
            <span style={{ fontSize: 11, color: '#aaa' }}>{r.date} · {r.car}</span>
          </div>
          {r.comment && <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>{r.comment}</div>}
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, display: 'flex', gap: 8 }}>
            <span>신속 {r.speed}★</span><span>정확 {r.accuracy}★</span><span>꼼꼼 {r.detail}★</span>
          </div>
        </div>
      ))}

      {/* 정산 내역 */}
      {mySettlements.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, marginTop: 6, color: '#222' }}>정산 내역</div>
          {mySettlements.map(function(s) {
            var isLoading = downloadingId === s.id;
            return (
              <div key={s.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 14, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>{s.year}년 {s.month}월</span>
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: s.status === 'settled' ? '#1D9E75' : '#D85A30', background: s.status === 'settled' ? '#E1F5EE' : '#FAECE7' }}>{s.status === 'settled' ? '정산완료' : '미정산'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4, display: 'flex', gap: 10 }}>
                  <span>{s.total_jobs}건</span>
                  <span>기본 {(s.base_fee || 0).toLocaleString()}</span>
                  <span>보너스 {(s.level_bonus || 0).toLocaleString()}</span>
                  <span style={{ fontWeight: 700, color: '#D85A30' }}>{(s.total_amount || 0).toLocaleString()}원</span>
                </div>
                {/* Wave S5(b) (2026-05-21) — 본인 정산서 PDF 다운로드 */}
                <button onClick={function() { handleDownloadPdf(s.id); }} disabled={isLoading} style={{ width: '100%', marginTop: 8, padding: '10px 0', borderRadius: 8, border: '1.5px solid #1B3A5C', background: '#fff', color: '#1B3A5C', fontSize: 13, fontWeight: 700, cursor: isLoading ? 'default' : 'pointer' }}>
                  {isLoading ? '발급 중...' : '📄 정산서 PDF 다운로드'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 로그아웃 */}
      {onLogout && (
        <button onClick={onLogout} style={{
          width: '100%', marginTop: 20, padding: '14px 0', borderRadius: 12,
          border: '1px solid #ddd', background: '#fff', color: '#888',
          fontSize: 14, cursor: 'pointer',
        }}>로그아웃</button>
      )}
    </div>
  );
}

export default ProfileView;
